import {
    forwardRef,
    useId,
    type CSSProperties,
    type ReactElement,
    type ReactNode,
    type SelectHTMLAttributes,
} from "react";
import { space } from "@tokens/index";
import { Text } from "@primitives/index";
import { adminSelectStyle } from "./styles";

/**
 * Field: a labelled form control. The label sits above the control on
 * every viewport class. `children` receives the id that the label points
 * at; the control must apply it.
 */

interface FieldProps {
    readonly label: string;
    readonly hint?: ReactNode;
    readonly children: (controlId: string) => ReactNode;
}

export function Field({ label, hint, children }: FieldProps): ReactElement {
    const id = useId();
    return (
        <div style={{ display: "grid", gap: space[1], minWidth: 0 }}>
            <Text as="label" role="bodySm" weight="medium" tone="muted" htmlFor={id}>
                {label}
            </Text>
            {children(id)}
            {hint !== undefined && (
                <Text as="p" role="caption" tone="muted">
                    {hint}
                </Text>
            )}
        </div>
    );
}

/**
 * AdminSelect: a native `<select>` in the admin control chrome. Full width
 * by default; pass `style` to bound it (in `ch` units, never fixed px).
 */

export interface AdminSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "style"> {
    readonly style?: CSSProperties;
}

export const AdminSelect = forwardRef<HTMLSelectElement, AdminSelectProps>(function AdminSelect(
    { style, disabled = false, ...rest },
    ref,
) {
    const computed: CSSProperties = {
        ...adminSelectStyle,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.56 : 1,
        ...style,
    };
    return <select ref={ref} disabled={disabled} style={computed} {...rest} />;
});
